// Governance → Compliance — the B4 cross-portfolio work cockpit. Replaces
// the old /compliance placeholder. Anatomy (five-second rule): summary
// strip → work queue (every B4-owned due/overdue item across all loans,
// deep-linking to the owning loan tab) → the "Annual legal reviews (LLCR)"
// section (the former Legal Review workspace queue; privilege rendering
// preserved exactly — see EngagementQueueSection). Read-only aggregation:
// all actions happen on the loan tabs under their existing gates.

import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Card, EmptyState, FilterChips, SearchField, SortSelect, Table, TBody, TD, TH, THead, TR } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useComplianceWorkQueue } from "@/hooks/compliance/useWorkQueues";
import { extractErrorMessage } from "@/api/client";
import type {
  ComplianceWorkQueueRowDto,
  ComplianceWorkQueueType,
} from "@/api/compliance/workQueueTypes";
import {
  dueDelta,
  SeverityDot,
  sortQueueRows,
  SummaryStrip,
} from "@/components/governance/WorkQueuePrimitives";
import { Pagination } from "@/components/layout/Pagination";
import { EngagementQueueSection } from "@/components/compliance/legalReview/EngagementQueueSection";

export const COMPLIANCE_TYPE_LABEL: Record<ComplianceWorkQueueType, string> = {
  Insurance: "Insurance",
  Ucc: "UCC",
  LegalCheck: "Legal check",
  Exception: "Exception",
  MaterialEvent: "Material event",
  ReportDue: "Report due",
  VendorCovenant: "Vendor covenant",
  Claim: "Claim",
  ReportedIssue: "Reported issue",
};

/** Reported-issue rows deep-link straight into the report drawer on the
 *  loan Compliance tab (row id = "intake-report-{reportId}"). */
const INTAKE_REPORT_ROW_PREFIX = "intake-report-";

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "urgency", label: "Urgency" },
  { value: "due", label: "Due date" },
  { value: "loan", label: "Loan" },
];

export default function ComplianceCockpitPage() {
  const auth = useAuth();
  const isPrivileged =
    auth.hasRoleCode("A3") ||
    auth.hasRoleCode("E4") ||
    auth.hasRoleCode("ADMIN");
  const preContribution = useFeatureFlag("PreContributionScope");

  const [params, setParams] = useSearchParams();
  const typeFilter = params.get("type") ?? "All";
  const severityFilter = params.get("severity") ?? "All";
  const loanSearch = params.get("loan") ?? "";
  const sort = params.get("sort") ?? "urgency";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const section = params.get("section");

  const writeParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    next.delete("page");
    setParams(next, { replace: true });
  };
  const setParam = (key: string, value: string, defaultValue: string) =>
    writeParams((p) => {
      if (value === defaultValue) p.delete(key);
      else p.set(key, value);
    });
  const setPage = (next: number) =>
    setParams(
      (p) => {
        const out = new URLSearchParams(p);
        out.set("page", String(next));
        return out;
      },
      { replace: true },
    );

  const queueQuery = useComplianceWorkQueue();

  // The loan Compliance tab's "Open workspace" cross-link lands on
  // ?section=legal-reviews — scroll the attorney section into view.
  useEffect(() => {
    if (section === "legal-reviews" && !queueQuery.isLoading) {
      document
        .getElementById("legal-reviews")
        ?.scrollIntoView({ block: "start" });
    }
  }, [section, queueQuery.isLoading]);

  const allRows = useMemo(() => {
    const rows = queueQuery.data?.rows ?? [];
    // Pool-scope items are hidden in the pre-contribution period (no pools
    // exist) — same rule as the attorney section's pool filter.
    return preContribution ? rows.filter((r) => !r.poolScope) : rows;
  }, [queueQuery.data, preContribution]);

  const filtered = useMemo(() => {
    const search = loanSearch.trim().toLowerCase();
    const rows = allRows
      .filter((r) => (typeFilter === "All" ? true : r.type === typeFilter))
      .filter((r) =>
        severityFilter === "All" ? true : r.severity === severityFilter,
      )
      .filter((r) =>
        search
          ? (r.loanNumber ?? "").toLowerCase().includes(search)
          : true,
      );
    return sortQueueRows(rows, sort);
  }, [allRows, typeFilter, severityFilter, loanSearch, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const summary = queueQuery.data?.summary;

  return (
    <div className="flex flex-col gap-space-4" data-testid="compliance-cockpit">
      <Card>
        <h1 className="font-display font-bold text-h2 m-0">Compliance</h1>
        <p className="text-caption text-gray-mid mt-space-1 m-0">
          Every compliance item B4 owns or that is due, across the whole
          portfolio. Work happens on the loan tabs — rows link through.
        </p>
      </Card>

      {summary && (
        <SummaryStrip
          data-testid="compliance-summary-strip"
          tiles={[
            { id: "due-week", label: "Due this week", value: summary.dueThisWeek, tone: "warning" },
            { id: "overdue", label: "Overdue", value: summary.overdue, tone: "error" },
            { id: "exceptions", label: "Open exceptions", value: summary.openExceptions },
            { id: "material-events", label: "Open material events", value: summary.openMaterialEvents },
            { id: "attorney-reviews", label: "Attorney reviews in progress", value: summary.attorneyReviewsInProgress },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-col gap-space-3">
          <FilterChips
            label="Filter by type"
            chips={[
              { id: "All", label: "All types" },
              ...Object.entries(COMPLIANCE_TYPE_LABEL).map(([id, label]) => ({
                id,
                label,
              })),
            ]}
            activeId={typeFilter}
            onChange={(id) => setParam("type", id, "All")}
          />
          <div className="flex flex-wrap items-center gap-space-3">
            <FilterChips
              label="Filter by severity"
              chips={[
                { id: "All", label: "All severities" },
                { id: "error", label: "Overdue / critical" },
                { id: "warning", label: "Due soon" },
              ]}
              activeId={severityFilter}
              onChange={(id) => setParam("severity", id, "All")}
            />
            <SearchField
              label="Search by loan number"
              placeholder="Loan number…"
              value={loanSearch}
              onChange={(v) => setParam("loan", v, "")}
              className="w-56"
            />
            <SortSelect
              options={SORT_OPTIONS}
              value={sort}
              onChange={(v) => setParam("sort", v, "urgency")}
            />
          </div>
        </div>
      </Card>

      {queueQuery.isLoading ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading work queue…
          </p>
        </Card>
      ) : queueQuery.isError ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            {extractErrorMessage(queueQuery.error)}
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        allRows.length === 0 ? (
          <div data-testid="compliance-queue-empty">
            <EmptyState
              title="Nothing needs attention"
              description="No compliance items are due, overdue, or open across the portfolio."
            />
          </div>
        ) : (
          <div data-testid="compliance-queue-filtered-empty">
            <EmptyState
              variant="noResults"
              title="No items match the current filters"
              description="Clear the type, severity, or loan filters to see the full queue."
            />
          </div>
        )
      ) : (
        <Card className="p-0 overflow-x-auto">
          <Table data-testid="compliance-queue-table">
            <THead>
              <TR>
                <TH className="w-8"><span className="sr-only">Severity</span></TH>
                <TH>Item</TH>
                <TH>Loan</TH>
                <TH>Due</TH>
              </TR>
            </THead>
            <TBody>
              {pageRows.map((r) => (
                <QueueRow key={r.id} row={r} />
              ))}
            </TBody>
          </Table>
          {pageCount > 1 && (
            <div className="px-space-3 pb-space-3">
              <Pagination
                page={currentPage}
                pageSize={PAGE_SIZE}
                totalCount={filtered.length}
                totalPages={pageCount}
                onPrev={() => setPage(currentPage - 1)}
                onNext={() => setPage(currentPage + 1)}
              />
            </div>
          )}
        </Card>
      )}

      {/* The attorney section reads its own filters (loanId / poolId /
          scope / year / attorney / reviewStatus) from the query string. */}
      <EngagementQueueSection isPrivileged={isPrivileged} />
    </div>
  );
}

function QueueRow({ row }: { row: ComplianceWorkQueueRowDto }) {
  const reportParam =
    row.type === "ReportedIssue" && row.id.startsWith(INTAKE_REPORT_ROW_PREFIX)
      ? `&report=${row.id.slice(INTAKE_REPORT_ROW_PREFIX.length)}`
      : "";
  const target = row.loanId
    ? `/portfolio-admin/loans/${row.loanId}?tab=${row.targetTab}${reportParam}`
    : null;
  return (
    <TR data-testid={`compliance-queue-row-${row.id}`}>
      <TD>
        <SeverityDot severity={row.severity} />
      </TD>
      <TD>
        <div className="flex items-center gap-space-2">
          <Badge status="archived">{COMPLIANCE_TYPE_LABEL[row.type]}</Badge>
          {row.type === "ReportedIssue" && row.source === "Borrower" && (
            <Badge status="info">Borrower</Badge>
          )}
          {target ? (
            <Link
              to={target}
              className="font-semibold text-small text-onyx hover:text-gold-shadow hover:underline"
              data-testid={`compliance-queue-link-${row.id}`}
            >
              {row.title}
            </Link>
          ) : (
            <span className="font-semibold text-small">{row.title}</span>
          )}
        </div>
        {row.detail && (
          <p className="text-caption text-gray-mid mt-space-1 m-0 truncate max-w-xl">
            {row.detail}
          </p>
        )}
      </TD>
      <TD>
        {row.loanId ? (
          <Link
            to={target!}
            className="text-small text-gold-shadow hover:underline"
          >
            {row.loanNumber ?? "View loan"}
          </Link>
        ) : (
          <span className="text-caption text-gray-mid">—</span>
        )}
      </TD>
      <TD className="whitespace-nowrap text-small">
        {/* The viewer's zone, not `dueDelta`'s Phoenix fallback: `dueDate` is a
            wall-calendar `DateOnly`, so "how many days until it" is a question
            about the day the reader is standing in. */}
        {dueDelta(row.dueDate)}
      </TD>
    </TR>
  );
}
